/**
 * Browser-side image resizing helper.
 *
 * Modern phone cameras produce 5-15 MB+ HEIC/JPEG photos. We resize/compress
 * to a max dimension of 1600px and re-encode as JPEG at ~85% quality, which:
 *   - keeps payloads small (<500 KB typically)
 *   - is the sweet spot for GPT-4o vision (it doesn't need huge images)
 *   - works around mobile upload size limits and S3/FastAPI body caps
 *
 * Heads-up: HEIC images (iPhone default) can't be drawn to a <canvas> in most
 * browsers — Safari can, Chrome/Firefox can't. If we can't decode, we fall
 * back to passing the original file through so the user still gets an error
 * they can act on instead of a silent failure.
 */
export async function resizeImageFile(file, maxDim = 1600, quality = 0.85) {
  if (!file || !file.type?.startsWith('image/')) return file;

  // Try to load the image — if the browser can't decode it (HEIC on Chrome
  // Android), bail out and return the original file unchanged.
  let img;
  try {
    img = await new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const i = new Image();
      i.onload = () => { URL.revokeObjectURL(url); resolve(i); };
      i.onerror = () => { URL.revokeObjectURL(url); reject(new Error('decode failed')); };
      i.src = url;
    });
  } catch {
    return file;
  }

  // If the image is already small enough, skip the canvas round-trip
  if (img.width <= maxDim && img.height <= maxDim && file.size <= 1024 * 1024) {
    return file;
  }

  const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, w, h);

  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
  if (!blob) return file;

  // Use a stable filename — drop the original extension to ensure jpeg
  const baseName = (file.name || 'meal').replace(/\.[^.]+$/, '');
  return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
}
